import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, FileSignature, Users } from 'lucide-react';
import { GroupContractItemDto, IdLabel } from 'api/contract';
import { Button } from 'components/ui/Button';
import MoreBubble from 'components/ui/Bubble';
import { EyeIcon } from 'components/icons/Icons';
import { GroupContractStepIdentifier } from 'back-office/features/group-contract-process/types/enums';
import {
  BROKER_PROCESS_PREFIX,
  buildProcessRoute,
} from 'back-office/features/group-contract-process/utils/getRouteByStep';
import {
  formatDate,
  renderStatusBadge,
  STATUT_BROUILLON_CODE,
  STATUT_EN_COURS_CODE,
} from 'features/group-contract-view/components/GroupContractTable';
import { contractsAPI } from 'services/swagger-api';
import ServerTable from 'shared/contexts/table-context/components/ServerTable';

interface PersonGroupContractsProps {
  personId: string;
}

/**
 * Onglet « Contrats collectifs » de la section Contrats de la fiche client : liste
 * les contrats de groupe dont le client est souscripteur (filtre personIds côté back).
 * Colonnes alignées sur GroupContractTable, sans la colonne Souscripteur (c'est le
 * client courant). Rendu comme contenu d'onglet dans PersonContracts — pas de carte.
 */
export const PersonGroupContracts: React.FC<PersonGroupContractsProps> = ({ personId }) => {
  const navigate = useNavigate();

  const handleView = useCallback((groupContract: GroupContractItemDto) => {
    // Contrat actif : page de détail ; brouillon/en cours de création : reprise du process.
    if (groupContract.statusCode === STATUT_EN_COURS_CODE) {
      navigate(`/mes-contrats-collectifs/${groupContract.id}`);
      return;
    }
    const isBrouillon = groupContract.statusCode === STATUT_BROUILLON_CODE;
    navigate(
      buildProcessRoute(
        groupContract.id,
        isBrouillon
          ? GroupContractStepIdentifier.Subscriber
          : GroupContractStepIdentifier.Contractualization,
        BROKER_PROCESS_PREFIX,
      ),
    );
  }, [navigate]);

  const fetchGroupContracts = useCallback(async (params: any) => {
    const response = await contractsAPI.getGroupContractsCourtier({
      getGenericListCommandDto: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
        searchString: params.searchString || '',
        sorting: params.sorting,
        filters: { personIds: [personId] },
      },
    });

    return {
      items: response.items,
      totalItems: response.totalCount,
      totalPages: Math.ceil(response.totalCount / params.pageSize),
      currentPage: params.pageNumber,
    };
  }, [personId]);

  return (
    <ServerTable<GroupContractItemDto>
          apiConfig={{ fetchData: fetchGroupContracts }}
          columns={[
            {
              key: 'contractIdentifier',
              label: 'Identifiant',
              sortable: true,
              width: '200px',
              render: (value: any) =>
                value ? (
                  <span className="font-mono font-semibold text-gray-900">{String(value)}</span>
                ) : (
                  <span className="text-gray-400 italic text-sm">Brouillon non finalisé</span>
                ),
            },
            {
              key: 'status',
              label: 'Statut',
              sortable: true,
              width: '120px',
              render: (value: any) => renderStatusBadge(String(value || '')),
            },
            {
              key: 'isMaster',
              label: 'Type',
              sortable: true,
              align: 'center',
              width: '110px',
              render: (value: any) =>
                value ? (
                  <div className="flex items-center justify-center gap-2">
                    <Crown className="text-yellow-600" size={16} />
                    <span className="text-sm font-medium text-yellow-700">Cadre</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2">
                    <Users className="text-primary" size={16} />
                    <span className="text-sm font-medium text-gray-700">Groupe</span>
                  </div>
                ),
            },
            {
              key: 'products',
              label: 'Produits',
              sortable: false,
              render: (value: any) => {
                const items = (value as IdLabel[] | undefined) ?? [];
                if (items.length === 0) {
                  return <span className="text-gray-400">-</span>;
                }
                return (
                  <MoreBubble
                    items={items.map((p) => ({ label: p.label }))}
                  />
                );
              },
            },
            {
              key: 'mainDueDate',
              label: 'Échéance',
              sortable: false,
              width: '140px',
              align: 'center',
              render: (value: any) => (
                <span className="text-sm text-gray-700">{value || '-'}</span>
              ),
            },
            {
              key: 'activeContractsCount',
              label: 'Contrats actifs',
              sortable: true,
              align: 'center',
              width: '120px',
              render: (value: any) => (
                <div className="flex items-center justify-center gap-2">
                  <FileSignature className="text-primary" size={16} />
                  <span className="font-semibold text-gray-900">{String(value ?? 0)}</span>
                </div>
              ),
            },
            {
              key: 'creationTime',
              label: 'Créé le',
              sortable: true,
              width: '120px',
              render: (value: any) => (
                <span className="text-sm text-gray-600">{formatDate(value)}</span>
              ),
            },
          ]}
          actions={{
            row: [
              {
                label: 'seeMore',
                render: (row: GroupContractItemDto, index: number) => (
                  <Button
                    key={index}
                    color="slate"
                    round
                    onClick={() => handleView(row)}
                  >
                    <EyeIcon />
                  </Button>
                ),
              },
            ],
          }}
          onRowClick={(row: GroupContractItemDto) => handleView(row)}
          selectable={false}
          pageSize={10}
          pageSizeOptions={[5, 10, 20, 50]}
          showPaginationInfo
          enableSearch
          searchPlaceholder="Rechercher un contrat collectif..."
          skeletonRowCount={5}
        />
  );
};
